import { useInfiniteQuery } from '@tanstack/react-query';
import { api, LitSquadInventoryEntry, LitSquadInventoryListResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseLitSquadInventoryOptions {
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface UseLitSquadInventoryReturn {
  items: LitSquadInventoryEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useLitSquadInventory(
  options: UseLitSquadInventoryOptions = {},
): UseLitSquadInventoryReturn {
  const search = options.search || undefined;
  const sortBy = options.sortBy || 'name';
  const sortOrder = options.sortOrder || 'asc';

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.litSquad.inventory({ search, sortBy, sortOrder }),
      queryFn: ({ pageParam = 0 }): Promise<LitSquadInventoryListResult> =>
        api.getLitSquadInventory({
          search,
          sortBy,
          sortOrder,
          limit: LIMIT,
          offset: pageParam,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
    });

  const allItems = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    items: allItems,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load inventory' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
  };
}
